import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import { Menu, MenuItem, MenuProps, useTheme } from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { useDataProvider, useNotify } from 'react-admin';
import { useMutation } from '@tanstack/react-query';
import { useCosmos } from 'providers/contexts/CosmosContext';
import React, {
  forwardRef,
  Fragment,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BaseMiniMenuProps } from './MiniMenuSelector';
import { bloxValues } from 'themes/constants';
import {
  ApiErrorResponse,
  BloxCustomDataProviderType,
  UserAccountSummary,
} from 'utility/types/dataProvider';
import { SimpleConfirmDeleteModal } from 'components/Modals/SimpleConfirmDeleteModal';
import { PermissionInfo } from 'utility/permissions';
import { QuickFilterEditModal } from 'components/SavedSearch/QuickFilterEditModal';
import {
  DeleteSavedSearchParams,
  DeleteSavedSearchResult,
  SavedSearchItem,
} from 'providers/contexts/extensions/SavedSearches';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';

export type MuiCloseEvent = Partial<Parameters<NonNullable<MenuProps['onClose']>>[0]>;
export type MuiCloseReasons = Partial<Parameters<NonNullable<MenuProps['onClose']>>[1]>;

export interface QuickFilterMiniMenuConfig {
  edit?: { enabled?: boolean };
  delete?: { enabled?: boolean };
}

export const allQuickFilterMenuOptions: QuickFilterMiniMenuConfig = {
  edit: { enabled: true },
  delete: { enabled: true },
};

// GenericSearchItem[] but in here should safely
// be able to cast SavedSearchItem[]
export interface QuickFilterMiniMenuProps extends Omit<
  BaseMiniMenuProps,
  'selectedItems' | 'contextMenuItem'
> {
  selectedItems: SavedSearchItem[];
  menuConfig?: QuickFilterMiniMenuConfig;
  contextMenuItem?: SavedSearchItem; // The item that was right-clicked
  isContextItemDisabled?: boolean; // True if the context item is disabled
}

/**
 * Related Tickets:
 * CMSADMIN-1360
 *
 */

type ButtonState = {
  show: boolean; // option visibility based on config and user permissions
} & PermissionInfo;

export const QuickFilterMiniMenu = memo(
  forwardRef<HTMLDivElement, QuickFilterMiniMenuProps>(
    (
      {
        menuEvent,
        raResource,
        handleMiniMenuClose,
        clearSelectedItems,
        menuConfig = allQuickFilterMenuOptions,
        contextMenuItem,
        isContextItemDisabled,
        anchorEl,
      },
      ref,
    ) => {
      const theme = useTheme();
      const notify = useNotify();
      const { t } = useTranslation();
      const { queryClient } = useCosmosQueryClient();
      const dataProvider = useDataProvider<BloxCustomDataProviderType>();
      const { userInfo } = useCosmos();

      // Disable menu for shared items
      const currentUserUuid: UserAccountSummary['uuid'] = useMemo(() => {
        return userInfo?.uuid || '';
      }, [userInfo]);

      const hideMiniMenu = useMemo(() => {
        const isShared = Boolean(contextMenuItem?.shared);
        const isNotOwner = contextMenuItem?.user_id !== currentUserUuid;
        return isShared && isNotOwner;
      }, [contextMenuItem, currentUserUuid]);

      // mutation - delete a single saved search by id
      const { mutateAsync: mutateDelete } = useMutation<
        DeleteSavedSearchResult,
        ApiErrorResponse,
        DeleteSavedSearchParams
      >({
        mutationKey: ['deleteSavedSearch', raResource],
        mutationFn: async (params: DeleteSavedSearchParams) => {
          return await dataProvider.deleteSavedSearch(params);
        },
        onSuccess: async (
          data: DeleteSavedSearchResult,
          variables: DeleteSavedSearchParams,
          context: unknown,
        ) => {
          queryClient.invalidateQueries({ queryKey: [raResource] });
        },
        onError: async (error: ApiErrorResponse) => {
          notify(
            `${t('blox.general.error.label')}: ${error?.message || error?.axiosMessage}`,
            { type: 'error' },
          );
        },
      });

      // Determine menu mode: button (anchorEl) or context (menuEvent position)
      const isButtonMode = Boolean(anchorEl);

      // Minimenu UI States
      const menuX = menuEvent?.clientX ?? undefined;
      const menuY = menuEvent?.clientY ?? undefined;
      const hasXY = menuX && menuY;
      const isOpen = isButtonMode ? Boolean(anchorEl) : Boolean(hasXY);
      const anchorPos: MenuProps['anchorPosition'] = hasXY
        ? { top: menuY, left: menuX }
        : undefined;

      // For saved searches, we only delete one at a time (the right-clicked item)
      const quickFilterItem = contextMenuItem;
      const itemId = quickFilterItem?.id;
      const confirmDeleteText = t('blox.search.saved.delete_confirm', {
        name: quickFilterItem?.name || t('blox.search.saved.this_quick_filter'),
      });

      // --------------------------------------------------
      // Event Handlers, Styles
      // --------------------------------------------------
      const iconSx: SxProps<Theme> = useMemo(() => {
        return {
          fontSize: bloxValues.iconSizeRegular,
          color: theme.palette.text.secondary,
          mr: 1,
        };
      }, [theme]);

      // for subsequent right clicks on background items
      // while menu open we will do some tricks hiding backdrop,
      // pass pointer events through, removing transitions
      // and add a few capture listeners to handle event/close
      // also tried this with <Popper> as well but the scroll locking
      // feature that menu has felt better than without it
      const menuSx: SxProps<Theme> = useMemo(() => {
        return {
          '& .MuiMenuItem-root': {
            fontSize: bloxValues.regularFontSizeRem,
          },
        };
      }, []);

      const menuListRef = useRef<HTMLUListElement>(null);

      const internalOnClose = useCallback(
        (event?: MuiCloseEvent, reason?: MuiCloseReasons) => {
          handleMiniMenuClose();
        },
        [handleMiniMenuClose],
      );

      const handleMenuClickCaptures = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
          // if click is outside the menu prevent bubbling/default
          // and then forward to internalOnClose to handle close
          if (!menuListRef?.current?.contains(e?.target as Node)) {
            e.stopPropagation();
            e.preventDefault();
            internalOnClose(e, 'backdropClick');
            return;
          }
          // else - the click happened inside the menu
          // so let event pass to menu click handlers
          // and have them handle event as needed
        },
        [internalOnClose],
      );

      // --------------------------------------------------
      // Edit Button
      // --------------------------------------------------

      const editState: ButtonState = useMemo(() => {
        const showEdit = Boolean(menuConfig?.edit?.enabled);
        return {
          disabled: !quickFilterItem,
          show: showEdit,
        };
      }, [quickFilterItem, menuConfig?.edit?.enabled]);

      const openEditDialog = useCallback(() => {
        setConfirmEditOpen(true);
        internalOnClose();
      }, [internalOnClose]);

      const closeEditDialog = useCallback(() => {
        setConfirmEditOpen(false);
      }, []);

      const handleEditCancel = useCallback(() => {
        closeEditDialog();
      }, [closeEditDialog]);

      const handleOpenEdit = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event.preventDefault();
          if (editState?.disabled) return;
          openEditDialog();
        },
        [editState, openEditDialog],
      );

      // --------------------------------------------------
      // Delete Button
      // Saved searches can only be deleted one at a time
      // --------------------------------------------------

      const deleteBtnState: ButtonState = useMemo(() => {
        const showDelete = Boolean(menuConfig?.delete?.enabled);

        return {
          disabled: isContextItemDisabled || !quickFilterItem || !itemId,
          show: showDelete,
        };
      }, [isContextItemDisabled, menuConfig?.delete?.enabled, quickFilterItem, itemId]);

      const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
      const [confirmEditOpen, setConfirmEditOpen] = useState(false);

      const openDeleteDialog = useCallback(() => {
        setConfirmDeleteOpen(true);
        internalOnClose();
      }, [internalOnClose]);

      const closeDeleteDialog = useCallback(() => {
        setConfirmDeleteOpen(false);
      }, []);

      // confirm delete action in dialog
      const handleDeleteConfirm = useCallback(async () => {
        if (!itemId) {
          console.error('[handleDeleteConfirm] No saved search id to delete.');
          closeDeleteDialog();
          return;
        }
        // delete the saved search
        try {
          await mutateDelete({
            path: {
              id: itemId,
            },
          });
          notify(t('blox.search.saved.delete_success'), { type: 'success' });
        } catch (e) {
          console.error('[handleDeleteConfirm] Error:', e);
        } finally {
          clearSelectedItems();
          closeDeleteDialog();
        }
      }, [t, notify, itemId, mutateDelete, clearSelectedItems, closeDeleteDialog]);

      // cancel delete action in dialog
      const handleDeleteCancel = useCallback(() => {
        closeDeleteDialog();
      }, [closeDeleteDialog]);

      // open delete dialog from minimenu
      const handleDeleteMenuClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          if (deleteBtnState?.disabled) return;
          openDeleteDialog();
        },
        [deleteBtnState?.disabled, openDeleteDialog],
      );

      if (hideMiniMenu) {
        return null;
      }

      return (
        <Fragment>
          <SimpleConfirmDeleteModal
            open={confirmDeleteOpen}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            subTxt={confirmDeleteText}
            initialWidth={350}
            initialHeight={215}
            isDraggable={true}
          />
          <QuickFilterEditModal
            payload={quickFilterItem}
            open={confirmEditOpen}
            onCancel={handleEditCancel}
          />
          <Menu
            ref={ref}
            data-testid="quickfilter-minimenu"
            disableEnforceFocus={true}
            open={isOpen}
            onClose={internalOnClose}
            sx={menuSx}
            hideBackdrop={true}
            onMouseDownCapture={handleMenuClickCaptures}
            onContextMenuCapture={handleMenuClickCaptures}
            {...(isButtonMode
              ? { anchorEl }
              : {
                  anchorReference: 'anchorPosition' as const,
                  anchorPosition: anchorPos,
                  TransitionProps: { timeout: { enter: 0, exit: 0 } },
                })}
            slotProps={{
              list: {
                autoFocusItem: true,
                ref: menuListRef,
              },
            }}
          >
            {editState?.show ? (
              <MenuItem
                data-testid="quickfilter-minimenu-edit"
                disabled={editState?.disabled}
                onClick={handleOpenEdit}
              >
                <EditIcon sx={iconSx} />
                {t('blox.actions.edit')}
              </MenuItem>
            ) : null}

            {deleteBtnState?.show ? (
              <MenuItem
                data-testid="quickfilter-minimenu-delete"
                onClick={handleDeleteMenuClick}
                disabled={deleteBtnState?.disabled}
              >
                <DeleteForeverIcon sx={iconSx} />
                {t('blox.actions.delete')}
              </MenuItem>
            ) : null}
          </Menu>
        </Fragment>
      );
    },
  ),
);

QuickFilterMiniMenu.displayName = 'QuickFilterMiniMenu';
